// What a save's confirms did to the export, as the API's answer says (`export` on the engine answer,
// services/Paige.Api/Engine/ReviewSaveGate.cs). Once a document has been exported, Paige is done with it
// (ADR-0031): a Confirm of an exported document saves the corrections for Cortexa to learn from and sends
// nothing again. The person is told so, since nothing on the screen says it otherwise.
// The registry lives beside the adapter for the same reason as review-versions.ts: the adapter is a
// plain function the ported screen calls, and the notice is a component that listens.

/**
 * The answer's `export`: first exports queued; confirmed documents that had already gone out (nothing sent
 * again); and corrected documents sent to the destinations that had refused them (`sentTo`, their names).
 */
export interface ExportOutcome {
  accepted: number;
  kept: number;
  resent: number;
  sentTo: string[];
}

const listeners = new Set<(line: string) => void>();

/** Tells the screen a save confirmed documents that were already exported; returns the unsubscribe. */
export function onExportedOnce(listener: (line: string) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The line for the person; null when every confirmed document was a first export (a first Confirm says nothing new). */
export function exportedOnceLine(answer: Partial<ExportOutcome> | undefined): string | null {
  const sentTo = (answer?.sentTo ?? []).filter(
    (name) => typeof name === 'string' && name.length > 0,
  );
  if (count(answer?.resent) > 0 && sentTo.length > 0)
    return `Sent to ${names(sentTo)}, where it had not landed. Nowhere else.`;
  const kept = count(answer?.kept);
  if (kept === 0) return null;
  const who = kept === 1 ? 'Already exported' : `${kept} documents already exported`;
  return `${who}: not sent again. Your corrections teach Cortexa.`;
}

/** Reads an engine answer and tells the listeners when it confirmed documents that were already exported. */
export function readExportAnswer(body: { export?: Partial<ExportOutcome> }): void {
  const line = exportedOnceLine(body.export);
  if (!line) return;
  for (const listener of listeners) listener(line);
}

/** "A", "A and B", "A, B and C". */
export function names(list: readonly string[]): string {
  if (list.length <= 1) return list[0] ?? '';
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/**
 * What a Confirm of a document a destination refused will do (ADR-0031): it goes to the destinations where it
 * never landed, and nowhere else. Null when no destination refused it.
 */
export function sendsToWords(
  refusedAt: readonly string[] | undefined,
  notYet: readonly string[] | undefined,
): string | null {
  if (!refusedAt || refusedAt.length === 0) return null;
  const rest =
    notYet && notYet.length > 0 ? `, and to ${names(notYet)}, where it has not gone yet` : '';
  return `Will be sent to ${names(refusedAt)}, where it was refused${rest}. Nowhere else.`;
}

const count = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
